import { useEffect, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styles from "./Invite.module.css";

import { ChevronDown } from "../../../common/Icons/Icons";
import { useSaveSubjectPlanAccess } from "../../../features/subjectPlan/hooks/useSaveSubjectPlanAccess";
import { useSaveYearPlanAccess } from "../../../features/yearPlan/hooks/useSaveYearPlanAccess";
import { useMyGroupMemberships, useGroupTree } from "../../../api/hooks";
import GroupPicker from "../GroupPicker/GroupPicker";

const Invite = ({ planData, goToOverview, closeModal, planType }) => {
  const { t } = useTranslation("common");
  const methods = useForm();
  const [shareType, setShareType] = useState("person"); // "person" or "group"
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  const { mutate: saveSubjectPlanAccess } = useSaveSubjectPlanAccess();
  const { mutate: saveYearPlanAccess } = useSaveYearPlanAccess();
  const { data: membershipsData } = useMyGroupMemberships();

  const groupMemberships = membershipsData?.data || [];

  // Get organizations where user has admin access
  const adminOrganizations = useMemo(() => {
    return groupMemberships.filter(m =>
      m.role?.name === "admin" && m.group?.type === "organization"
    );
  }, [groupMemberships]);

  // Fetch group trees for each admin organization (max 5 to avoid too many hooks)
  // Use includeHidden: false to exclude hidden groups
  const tree1 = useGroupTree(adminOrganizations[0]?.group?.id, false, {
    enabled: !!adminOrganizations[0]?.group?.id
  });
  const tree2 = useGroupTree(adminOrganizations[1]?.group?.id, false, {
    enabled: !!adminOrganizations[1]?.group?.id
  });
  const tree3 = useGroupTree(adminOrganizations[2]?.group?.id, false, {
    enabled: !!adminOrganizations[2]?.group?.id
  });
  const tree4 = useGroupTree(adminOrganizations[3]?.group?.id, false, {
    enabled: !!adminOrganizations[3]?.group?.id
  });
  const tree5 = useGroupTree(adminOrganizations[4]?.group?.id, false, {
    enabled: !!adminOrganizations[4]?.group?.id
  });

  const orgTrees = [tree1, tree2, tree3, tree4, tree5];

  // Build list of all shareable groups, organized by parent organization
  const groupsByOrganization = useMemo(() => {
    const orgMap = new Map(); // organizationId -> { org, groups: [] }
    const groupIds = new Set();
    const standaloneGroups = []; // Groups without a known organization

    // Build tree structure from flat array with parentGroupId
    const buildTree = (flatData, rootId) => {
      const nodeMap = new Map();
      const rootChildren = [];

      // First pass: create all nodes
      flatData.forEach(item => {
        nodeMap.set(item.id, { ...item, children: [] });
      });

      // Second pass: link children to parents
      flatData.forEach(item => {
        const node = nodeMap.get(item.id);
        if (item.parentGroupId === rootId || item.parentGroupId === null) {
          // Direct child of root org or root itself
          if (item.id !== rootId) {
            rootChildren.push(node);
          }
        } else if (nodeMap.has(item.parentGroupId)) {
          nodeMap.get(item.parentGroupId).children.push(node);
        }
      });

      return rootChildren;
    };

    // Helper to flatten tree recursively with depth tracking
    const flattenTree = (node, orgId, depth = 0) => {
      if (!groupIds.has(node.id)) {
        groupIds.add(node.id);
        const groupData = {
          group: {
            id: node.id,
            name: node.name,
            type: node.type
          },
          depth
        };

        if (orgMap.has(orgId)) {
          orgMap.get(orgId).groups.push(groupData);
        }
      }
      if (node.children && node.children.length > 0) {
        // Sort children alphabetically before recursing
        node.children.sort((a, b) => a.name.localeCompare(b.name));
        node.children.forEach(child => flattenTree(child, orgId, depth + 1));
      }
    };

    // Add all groups from admin organization trees
    adminOrganizations.forEach((membership, index) => {
      if (index >= 5) return; // Only process first 5 organizations
      const flatData = orgTrees[index]?.data?.data || [];
      const orgId = membership.group.id;

      // Initialize organization entry
      if (!orgMap.has(orgId)) {
        orgMap.set(orgId, {
          org: membership.group,
          groups: []
        });
        groupIds.add(orgId);
      }

      // Build tree from flat data and flatten with proper depth
      const tree = buildTree(flatData, orgId);
      tree.sort((a, b) => a.name.localeCompare(b.name));
      tree.forEach(node => flattenTree(node, orgId, 0));
    });

    // Add groups where user has direct admin or write access (not already included)
    // Exclude hidden groups
    groupMemberships.forEach(membership => {
      if ((membership.role?.name === "admin" || membership.role?.name === "write") &&
          !groupIds.has(membership.group.id) &&
          !membership.group.hidden) {
        groupIds.add(membership.group.id);
        standaloneGroups.push({
          group: membership.group,
          depth: 0
        });
      }
    });

    // Sort standalone groups (tree groups are already sorted during traversal)
    standaloneGroups.sort((a, b) => a.group.name.localeCompare(b.group.name));

    // Convert to array and sort organizations by name
    const sortedOrgs = Array.from(orgMap.values()).sort((a, b) =>
      a.org.name.localeCompare(b.org.name)
    );

    return { organizations: sortedOrgs, standalone: standaloneGroups };
  }, [groupMemberships, adminOrganizations, orgTrees]);

  useEffect(() => {
    if (shareType === "person") {
      methods.reset({ email: "", access: "R", userId: null });
      setSelectedGroupId(null);
    } else {
      methods.reset({ email: "", access: "R", userId: null });
      setSelectedGroupId(null);
    }
  }, [methods, planData.id, shareType]);

  function handleCancelClick() {
    goToOverview();
  }

  function onSubmit(data) {
    let post;

    if (shareType === "person") {
      post = { email: data.email, access: data.access };
    } else {
      if (!selectedGroupId) {
        console.error("No group selected");
        return; // Don't submit if no group selected
      }
      post = { groupId: selectedGroupId, access: data.access };
    }

    if (planType === "subject") {
      saveSubjectPlanAccess(
        { data: post, id: planData.id },
        { onSuccess: closeModal }
      );
    } else if (planType === "year") {
      saveYearPlanAccess(
        { data: post, id: planData.id },
        { onSuccess: closeModal }
      );
    }
  }

  return (
    <section className={styles.container}>
      <div className={styles.tabs}>
        <button
          type="button"
          className={`${styles.tab} ${shareType === "person" ? styles.activeTab : ""}`}
          onClick={() => setShareType("person")}
        >
          {t("sharePlan.person")}
        </button>
        <button
          type="button"
          className={`${styles.tab} ${shareType === "group" ? styles.activeTab : ""}`}
          onClick={() => setShareType("group")}
        >
          {t("sharePlan.group")}
        </button>
      </div>

      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <section className={styles.inputs}>
          {shareType === "person" ? (
            <>
              <div>
                <label htmlFor="email">{t("email")}</label>
                <input
                  type="email"
                  id="email"
                  {...methods.register("email", { required: shareType === "person" })}
                />
              </div>

              <div>
                <select className={styles.select} {...methods.register("access")}>
                  <option value="A">{t("sharePlan.asAdmin")}</option>
                  <option value="W">{t("toEdit")}</option>
                  <option value="R">{t("toRead")}</option>
                </select>
                <ChevronDown className={styles.dropdownArrow} />
              </div>
            </>
          ) : (
            <>
              <div className={styles.groupPickerWrapper}>
                <label>{t("sharePlan.selectGroup")}</label>
                <GroupPicker
                  organizations={groupsByOrganization.organizations}
                  standalone={groupsByOrganization.standalone}
                  selectedGroupId={selectedGroupId}
                  onSelect={setSelectedGroupId}
                />
              </div>

              <div className={styles.accessSelect}>
                <label>{t("access")}</label>
                <div className={styles.selectWrapper}>
                  <select className={styles.select} {...methods.register("access")}>
                    <option value="W">{t("toEdit")}</option>
                    <option value="R">{t("toRead")}</option>
                  </select>
                  <ChevronDown className={styles.dropdownArrow} />
                </div>
              </div>
            </>
          )}
        </section>

        <button className={`${styles.button} ${styles.send}`} type="submit">
          {t("share")}
        </button>

        <button
          className={`${styles.button} ${styles.cancel}`}
          type="button"
          onClick={handleCancelClick}
        >
          {t("cancel")}
        </button>
      </form>
    </section>
  );
};

export default Invite;
